import * as THREE from 'three'

import Types from '../../types'
import GlassBaseHole from './../glassBaseHole'
import GlassSegment from '@/model/glassSegment'

import GlassDesignerTypes from '../../types'
import MathsUtils from '@/utils/mathsUtils'


/*
 * RectangleHole
 * Rectangle hole with rounded corners
 */
export default class RectangleHole extends GlassBaseHole {
  constructor(editor, options = {}) {
    super(editor, options)
    this.objectClassName = 'RectangleHole'
    
    this.name = 'Rectangle Center Cutout'
    this.holeType = GlassDesignerTypes.LibraryItemShapeTypes.rectangle
    this.defaultWidth = 100   // This is pointless...
    this.defaultHeight = 50
    
    let cornerRadius = options.cornerRadius || 0
    this.cornerRadiusTL = options.cornerRadiusTL || cornerRadius
    this.cornerRadiusTR = options.cornerRadiusTR || cornerRadius
    this.cornerRadiusBR = options.cornerRadiusBR || cornerRadius
    this.cornerRadiusBL = options.cornerRadiusBL || cornerRadius

    this.width = options.width || this.defaultWidth
    this.height = options.height || this.defaultHeight

    this.rebuildHoleShape()
  }

  /**
   * Update the center cutout settings
   */
  updateCutoutSettings(info) {
    if(info.width !== undefined)
      this.width = parseFloat(info.width || 0)
    if(info.height !== undefined)
      this.height = parseFloat(info.height || 0)

    if(info.cornerRadius !== undefined) {
      let cornerRadius = parseFloat(info.cornerRadius || 0)

      // Corner radius cannot be greater than 50% of the smallest side
      let minSide = Math.min(this.width, this.height)
      cornerRadius = Math.min(cornerRadius, Math.ceil(minSide / 2))

      this.setCornerRadius(cornerRadius)
    }
    

    super.updateCutoutSettings(info)
  }


  /**
   * Rotate the hole
   * Overriden by the shape classes
   */
  rotateHole(angleDegrees) {
    // Note: This process only works for 90 degree rotations. Which it currently is

    // Swap width/height. 
    var tmp = this.width
    this.width = this.height
    this.height = tmp

    super.rotateHole(angleDegrees)
  }


  /**
   * Rebuilds the hole segments based on the basic width/height of the rectangle hole
   * Only to be used when first creating the hole
   */
  rebuildHoleShape() {
    var startX = 0
    var startY = 0

    // Update via datum links
    this.updateDatumLinkWorldPoint()
    startX = this.worldPoint.x - this.anchorPoint.x
    startY = this.worldPoint.y - this.anchorPoint.y

    if(this.datumDisplayPointObj)
      this.datumDisplayPointObj.setPosition(this.worldPoint)

    this.segmentList = []

    // Rotate based on angle
    let origin = new THREE.Vector2(0, 0)
    let angleRads = MathsUtils.degreesToRadians(this.angle)
    let pointTL = MathsUtils.rotate2DPoint(origin, new THREE.Vector2(-this.width / 2.0, -this.height / 2.0), angleRads)
    let pointTR = MathsUtils.rotate2DPoint(origin, new THREE.Vector2(this.width / 2.0, -this.height / 2.0), angleRads)
    let pointBR = MathsUtils.rotate2DPoint(origin, new THREE.Vector2(this.width / 2.0, this.height / 2.0), angleRads)
    let pointBL = MathsUtils.rotate2DPoint(origin, new THREE.Vector2(-this.width / 2.0, this.height / 2.0), angleRads)

    var rotatedBounds = new THREE.Box2()
    rotatedBounds.expandByPoint(pointTL)
    rotatedBounds.expandByPoint(pointTR)
    rotatedBounds.expandByPoint(pointBR)
    rotatedBounds.expandByPoint(pointBL)
    
    let offset = new THREE.Vector2(startX + (rotatedBounds.max.x - rotatedBounds.min.x) / 2.0, startY + (rotatedBounds.max.y - rotatedBounds.min.y) / 2.0)

    pointTL.add(offset)
    pointTR.add(offset)
    pointBR.add(offset)
    pointBL.add(offset)

    let topSegment = new GlassSegment(Types.SegmentTypes.line, { startCornerRadius: this.cornerRadiusTL, endCornerRadius: this.cornerRadiusTR })
    topSegment.startPosition.copy(pointTL)
    topSegment.endPosition.copy(pointTR)

    let rightSegment = new GlassSegment(Types.SegmentTypes.line, { startCornerRadius: this.cornerRadiusTR, endCornerRadius: this.cornerRadiusBR })
    rightSegment.startPosition.copy(pointTR)
    rightSegment.endPosition.copy(pointBR)

    let bottomSegment = new GlassSegment(Types.SegmentTypes.line, { startCornerRadius: this.cornerRadiusBR, endCornerRadius: this.cornerRadiusBL })
    bottomSegment.startPosition.copy(pointBR)
    bottomSegment.endPosition.copy(pointBL)

    let leftSegment = new GlassSegment(Types.SegmentTypes.line, { startCornerRadius: this.cornerRadiusBL, endCornerRadius: this.cornerRadiusTR })
    leftSegment.startPosition.copy(pointBL)
    leftSegment.endPosition.copy(pointTL)

    this.segmentList.push(topSegment)
    this.segmentList.push(rightSegment)
    this.segmentList.push(bottomSegment)
    this.segmentList.push(leftSegment)

    MathsUtils.segmentListUpdated(this.segmentList)

    this.updateSceneObject()
  }


  /**
   * Checks if a 2D circle intersects with the rectangle hole
   * Returns true if an intersection occurred & sets the closest point on the hole the point is to
   */
  check2DCircleIntersection(point, radius, outIntPoint) {
    // Point inside?
    if(MathsUtils.pointIn2DPolygon(point, this.cachedPointList)) {
      outIntPoint.set(point.x, point.y)
      return true
    }

    // Closest point within the radius?
    let closestPoint = MathsUtils.calculateClosestPointOn2DPolygon(point, this.cachedPointList)
    if(!closestPoint)
      return false

    if(closestPoint.distanceTo(point) <= radius) {
      outIntPoint.set(closestPoint.x, closestPoint.y)
      return true
    }

    return false
  }


  /**
   * Clone the rectangle hole
   */
  clone() {    
    var newHole = new RectangleHole(this.editor)
    newHole.copy(this)

    return newHole
  }


  /**
   * Copy details from a rectangle hole to this rectangle hole
   */
  copy(otherHole) {
    super.copy(otherHole)
    
    this.defaultWidth = otherHole.defaultWidth
    this.defaultHeight = otherHole.defaultHeight

    this.cornerRadiusTL = otherHole.cornerRadiusTL
    this.cornerRadiusTR = otherHole.cornerRadiusTR
    this.cornerRadiusBR = otherHole.cornerRadiusBR
    this.cornerRadiusBL = otherHole.cornerRadiusBL

    this.width = otherHole.width
    this.height = otherHole.height
    this.rebuildHoleShape()
  }


  // Serialize/Deserialize to/from a JSON object
  serialize() {
    var objData = super.serialize()

    objData.defaultWidth = this.defaultWidth
    objData.defaultHeight = this.defaultHeight

    objData.cornerRadiusTL = this.cornerRadiusTL
    objData.cornerRadiusTR = this.cornerRadiusTR
    objData.cornerRadiusBR = this.cornerRadiusBR
    objData.cornerRadiusBL = this.cornerRadiusBL
    objData.width = this.width
    objData.height = this.height

    return objData
  }
  deserialize(object) {
    super.deserialize(object)

    this.defaultWidth = parseFloat(object.defaultWidth || 100)
    this.defaultHeight = parseFloat(object.defaultHeight || 50)

    this.cornerRadiusTL = parseFloat(object.cornerRadiusTL || 0)
    this.cornerRadiusTR = parseFloat(object.cornerRadiusTR || 0)
    this.cornerRadiusBR = parseFloat(object.cornerRadiusBR || 0)
    this.cornerRadiusBL = parseFloat(object.cornerRadiusBL || 0)

    this.width = parseFloat(object.width || this.defaultWidth)
    this.height = parseFloat(object.height || this.defaultHeight)

    this.holeSizeUpdated()
    this.rebuildHoleShape()
  }


  getCornerRadius() {
    // Gets the maximum corner radius for all 4 corners
    return Math.max(this.cornerRadiusTL, this.cornerRadiusTR, this.cornerRadiusBL, this.cornerRadiusBR)
  }

  // Sets all 4 corners to the same radius. For simple editing mode
  // Some code will update the radius individually for more precise control
  setCornerRadius(radius) {
    this.cornerRadiusTL = radius
    this.cornerRadiusTR = radius
    this.cornerRadiusBL = radius
    this.cornerRadiusBR = radius
  }
}
